import * as React from 'react';
import {useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import axios from "axios";
import {LAYOUT_CONSTANTS, LEAD_FORM, RESOURCE} from "../const";
import {setOffline} from "../redux/applicationActions";
import {RootState} from "../redux/store";
import useCurrentRoute from "../hooks/useCurrentRoute";
import analytics from "../../../shared/utility/analytics";
import {InputsContainer} from "../../../shared/containers";
import {Errors, Input} from "../../../shared/inputs";
import {PrimaryButton} from "../../../shared/buttons";
import {Loader} from "../../../shared/components";
import {ButtonContainer, LeadFormContainer} from "../../../shared/conversie";

const {FORM_ROW_PADDING} = LAYOUT_CONSTANTS;

interface Props {
  loading: boolean;
}

const LeadForm: React.FC<Props> = ({
  loading
}): React.ReactElement => {
  const pathname = useCurrentRoute();
  const dispatch = useDispatch();
  const [input, setInput] = useState({});
  const [busy, setBusy] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [errors, setErrors] = useState<any>({});
  const {
    currentArticle,
    leadFormEmailOnly,
    leadFormIntroduction,
    leadFormSuccessMessage,
    leadFormText,
    leadAddressInformation,
    showLeadButton
  } = useSelector((state: RootState) => {
    const {config, lead_form} = state.application;
    const articles = Object.values(state.entities.articles.byId);
    const {base} = config;
    const {showLeadButton, leadFormEmailOnly, leadFormIntroduction, leadFormSuccessMessage, leadFormText, leadAddressInformation} = lead_form;
    const slug = pathname && pathname.replace(base, '').replace(/\//g, '');
    const article = articles.find(article => article.slug === slug);

    return {
      currentArticle: article?.title,
      leadFormEmailOnly,
      leadFormIntroduction,
      leadFormSuccessMessage,
      leadFormText,
      leadAddressInformation,
      showLeadButton,
    }
  });

  if (leadAddressInformation) {
    LEAD_FORM.postal_code = ['text', 'Postcode'];
    LEAD_FORM.house_number = ['text', 'Huisnummer'];
    LEAD_FORM.house_number_addition = ['text', 'Toevoeging'];
  }

  const submitLead = e => {
    e.preventDefault();
    if (busy) return;

    let hasError = false;
    setBusy(true);
    const [method, url] = RESOURCE.lead;

    const data = {
      contact: null,
      contact_email: null,
      news_article_title: null,
      news_article_url: null,
      ...input
    };

    // separate phones and e-mail addresses
    if (!leadFormEmailOnly) {
      if (data.contact?.indexOf('@') > -1) {
        data.contact_email = data.contact;
        delete data.contact;
      } else {
        delete data.contact_email;
      }

    } else {
      delete data.contact_email;
    }

    // add article metadata
    data.news_article_url = window.location.href;
    data.news_article_title = currentArticle;

    axios.request({
      url,
      method,
      data
    })
      .then(res => {
        if (res.data && res.data.length === 0) res.data = {};
        analytics.send('Verstuur formulier', 'Lead');

        // display success message
        setShowSuccess(true);
        analytics.send('Verstuur formulier', 'Verstuur');
      })
      .catch(err => {
        const {message: error, errors} = err.response.data || {};

        // validation
        if (errors) {
          const {[leadFormEmailOnly ? 'contact' : 'contact_email']: ignore, ...rest} = errors; // eslint-disable-line @typescript-eslint/no-unused-vars

          // special handling for contact validation
          if (!leadFormEmailOnly) {
            if (errors.contact_email) {
              rest.contact = errors.contact_email;
            } else if (errors.contact) {
              rest.contact = errors.contact;
            }
          } else {
            if (errors.contact_email) {
              rest.contact_email = errors.contact_email;
            }
          }

          setErrors(rest);
        } else if (error) {
          setErrors({general: [`Bericht niet verstuurd: ${error}`]});
        }

        hasError = true;
      })
      .finally(() => {
        if (!hasError) {
          // reset form
          setInput({
            full_name: '',
            contact: '',
            contact_email: '',
            message: '',
          });

          dispatch(setOffline(false));
        }

        setBusy(false);
      });
  };

  return showLeadButton && (
    <LeadFormContainer>
      {loading && (
          <Loader style={{position: 'relative', flex: 1, marginBottom: FORM_ROW_PADDING}} />
      )}

      {!loading && leadFormIntroduction?.length ? (
        <p className={"f-5 theme--primary-font-color"} style={{marginBottom: FORM_ROW_PADDING}}>
          {leadFormIntroduction}
        </p>
      ) : null}

      {!loading && (
        showSuccess ? (
          <>
            <h2 className={"f-2 fw-600 theme--highlight-color"} style={{margin: `${FORM_ROW_PADDING} 0`}}>
              Bericht ontvangen
            </h2>
            {leadFormIntroduction?.length ? (
              <p className={"f-5 theme--primary-font-color"} style={{marginBottom: FORM_ROW_PADDING}}>
                {leadFormSuccessMessage}
              </p>
            ): null}
          </>
        ) : (
          <form
            onSubmit={submitLead}
            style={{
              width: 'inherit',
              marginBottom: FORM_ROW_PADDING,
              marginTop: '-15px', // offset input padding
            }}
          >
          <InputsContainer>
            {Object.entries(LEAD_FORM).map(([name, [type, label]], index) => {
              if ((leadFormEmailOnly && (name === 'contact')) || (!leadFormEmailOnly && (name === 'contact_email'))) {
                return;
              }

              return (
                <Input
                    id={name}
                    name={name}
                    key={name}
                    placeholder={label}
                    type={type}
                    value={input[name]}
                    final={(Object.keys(LEAD_FORM).length - 1) === index}
                    onChange={e => {
                      // reset error state
                      if (Object.keys(errors).indexOf(name) > -1) {
                        const {[name]: err, ...rest} = errors; // eslint-disable-line @typescript-eslint/no-unused-vars

                        setErrors(rest);
                      }

                      setInput({...input, [name]: e.target.value});
                    }}
                    className={"f-5 gutter--1-2-y theme--thumbnail-border-radius fw-600"}
                    style={{padding: `calc(${FORM_ROW_PADDING} / 1.5)`}}
                    errors={errors[name]}
                    inputRef={null}
                    showTextErrors={!!Object.keys(errors).length}
                    maxWidth={null}/>
              )
            })}

            <Errors errors={errors.general} />

           <ButtonContainer>
             <PrimaryButton
                   type="submit"
                   disabled={busy || !!Object.keys(errors).length}
                   style={{height: "56px"}}
                   className={"f-6 fw-600"}
               >
               {busy ? <Loader css={{ margin: 0 }} /> : "Verzenden"}
             </PrimaryButton>
           </ButtonContainer>

            {!loading && leadFormText?.length ? (
              <p className={"f-7 fw-600 theme--primary-color"} style={{marginTop: "10px"}}>
                {leadFormText}
              </p>
            ) : null}
            </InputsContainer>
          </form>
        )
      )}
    </LeadFormContainer>
  );
}

export default LeadForm;
